/**
 * The guided walkthrough — the seven-step tour a new player is offered once.
 *
 * Janeks' flow, verbatim:
 *
 * > Upon first login -> character creation -> when game ready -> show prompt
 * > with longer text about getting used to the game and controls -> cancel and
 * > Yes, show me
 * > 1. Controls - Walking around + run/walk toggle, + crouch toggle
 * > 2. looking around - mouse controls
 * > 3. Combat controls
 * > 3. highlight inventory button - tell that that is the pace where You keep
 * >    the stuff.
 * > 4. Quest journal - track quests and story
 * > 5. Map to go raiding to other places. There You can find real adventure
 * >    and put the character to the test. High risk = high reward.
 * > 6. Arena - Fight against others.
 * > 7 Chat.
 *
 * (His list numbers "3" twice — combat and the inventory are separate steps,
 * which makes eight cards behind the opening prompt.)
 *
 * ─── Why it lives HERE and not beside the Settings screen ───────────────────
 * Five of the eight steps highlight a real element — the action bar's
 * Inventory, Journal, Map, Arena and Chat buttons. Those are built by
 * `startBaseView` and destroyed with it on a Location visit, so an overlay
 * that outlived them would be drawing rings around nothing. It is created and
 * torn down with the view that owns its anchors, and the settlement is the
 * only view it runs in.
 *
 * Settings still starts it, through `walkthroughStore` rather than a function
 * call — see `requestWalkthrough`'s docstring for why that direction is the
 * only one available.
 *
 * ─── It is NOT a modal panel ────────────────────────────────────────────────
 * Deliberately unregistered with `ui/panels.ts`. A registration would mean
 * `closeOtherModals` shuts the tour the moment the player opens the very
 * window it just told them about, and `worldInputBlocked` would stop them
 * walking during the step that explains walking. The layer takes no pointer
 * events at all except on its own card (see `styles/tutorial.css.ts`), so
 * there is nothing to block: the highlighted button stays clickable, which is
 * the point of highlighting it.
 */
import {
  endWalkthrough, nextWalkthroughStep, prevWalkthroughStep, startWalkthrough,
  walkthroughStore, type WalkthroughState,
} from '../../state/tutorial-state';

export interface TutorialStep {
  /** Shown above the title — "step 3 of 8" is rendered separately. */
  kicker: string;
  title: string;
  /**
   * Paragraphs. Trusted, author-written HTML: the only markup used is
   * `<kbd>`, and nothing here comes from a player or from the server. The
   * legal reader next door goes through `markdown.ts` for exactly the
   * opposite reason — its content IS admin-editable.
   */
  body: string[];
  /**
   * CSS selector for the element to ring, or null for a step about something
   * with no single button (movement, the mouse, combat).
   *
   * Resolved at PAINT time, not at module load: the action bar is rebuilt on
   * every settlement mount, so a captured element would be stale after the
   * first Location visit.
   */
  anchor: string | null;
}

/**
 * The tour. Content, and the only place that knows how many steps there are —
 * `nextWalkthroughStep` is told the count rather than importing this, so the
 * two can never disagree about where the end is.
 */
export const TUTORIAL_STEPS: readonly TutorialStep[] = [
  {
    kicker: 'Moving',
    title: 'Getting around',
    body: [
      'Walk with <kbd>W</kbd> <kbd>A</kbd> <kbd>S</kbd> <kbd>D</kbd> or the arrow keys.',
      '<kbd>Caps Lock</kbd> switches between running and walking, and stays where you '
        + 'put it — the figure at the bottom of the screen shows which you are doing. '
        + '<kbd>C</kbd> crouches, and also stays put. Crouching is slower and quieter.',
    ],
    anchor: '.bv-gait',
  },
  {
    kicker: 'Looking',
    title: 'Looking around',
    body: [
      'Drag with the left mouse button to swing the camera around your survivor. '
        + 'The scroll wheel pulls it closer or further out.',
      'Left-click anything with a name to interact with it — a person to talk to, '
        + 'a door to go through, a container to search.',
    ],
    anchor: null,
  },
  {
    kicker: 'Fighting',
    title: 'Combat',
    body: [
      'Hold the <strong>right mouse button</strong> to raise your weapon. Your survivor '
        + 'turns to face wherever the cursor is pointing and stays facing it while you hold.',
      'With the weapon up, <strong>left-click</strong> or press <kbd>Space</kbd> to '
        + 'attack. Both work, so a trackpad is fine: holding the right button there '
        + 'takes two fingers and leaves none to click with. Space can be turned off '
        + 'under <strong>Settings &rarr; Controls</strong>, where the full list lives.',
      'Whichever you use, your weapon needs something in it — an empty-handed '
        + 'survivor swings for very little.',
    ],
    anchor: null,
  },
  {
    kicker: 'Carrying',
    title: 'Your inventory',
    body: [
      'This is where everything you own lives: what you are carrying, what you are '
        + 'wearing, and what is stored back at the hideout.',
      'Weight matters. Overload yourself and you slow down, so what you leave behind '
        + 'on a raid is as much of a decision as what you take.',
    ],
    anchor: '[data-action="inventory"]',
  },
  {
    kicker: 'Work',
    title: 'The quest journal',
    body: [
      'Everything anyone has asked of you is tracked here, along with how far through '
        + 'it you are and what it pays.',
      'The story runs through these too — the settlement tells you about itself one '
        + 'job at a time.',
    ],
    anchor: '[data-action="journal"]',
  },
  {
    kicker: 'Raiding',
    title: 'The map',
    body: [
      'The settlement is safe and the settlement is poor. Everything worth having is '
        + 'somewhere out there.',
      'Pick a place, take what you can carry, and get back. <strong>High risk, high '
        + 'reward</strong> — the further out you go the better the haul and the likelier '
        + 'you are to leave your kit on the floor of somewhere nobody will find you.',
    ],
    anchor: '[data-action="map"]',
  },
  {
    kicker: 'Fighting',
    title: 'The arena',
    body: [
      'Other survivors, one at a time, for rank. No loot at stake — only your record '
        + 'and your standing in the season.',
      'A good place to find out whether the build you have been putting together '
        + 'actually works.',
    ],
    anchor: '[data-action="arena"]',
  },
  {
    kicker: 'Talking',
    title: 'World chat',
    body: [
      'Everyone who is online, in one channel. Prices, warnings, and the occasional '
        + 'straight answer.',
      'That is the tour. You can run it again any time from <strong>Settings → '
        + 'Tutorial</strong>.',
    ],
    anchor: '[data-action="chat"]',
  },
];

/** The opening prompt — the "longer text about getting used to the game". */
const PROMPT = {
  kicker: 'Welcome to the settlement',
  title: 'Find your feet',
  body: [
    'You have come a long way to get here and you are going to be here a while. '
      + 'Before you wander off, it is worth a couple of minutes learning which button '
      + 'does what — the ones that keep you alive are not the obvious ones.',
    'Eight short cards: how to move, how to look, how to fight, and where the game '
      + 'keeps your things, your work, the road out and everyone else. You can stop '
      + 'part-way, and you can start it again from Settings whenever you like.',
  ],
};

/**
 * Where the ring goes when the step has nothing to point at.
 *
 * A zero-sized box AT THE CENTRE of the viewport, not off-screen. The dim is
 * drawn by the ring's own 200vmax box-shadow spread (see
 * `styles/tutorial.css.ts`), so parking it at `-9999px` takes the dimming with
 * it — the spread reaches ~3200px and never gets back to the screen. Caught in
 * a screenshot: the opening prompt floated over an undimmed settlement while
 * the anchored steps dimmed correctly, which is exactly the asymmetry an
 * off-screen park produces.
 *
 * Centred, the same spread covers the viewport in every direction, and a 0x0
 * transparent-bordered box draws nothing itself.
 */
const RING_NOWHERE = 'top:50%;left:50%;width:0;height:0';

export interface TutorialUI {
  dispose(): void;
}

/**
 * Mounts the overlay, hidden, and subscribes it to the store.
 *
 * `container` is the base view's own root, so the layer goes away with the
 * view. The element is `position: fixed` regardless, so where it hangs makes
 * no visual difference — what it buys is teardown.
 */
export function createTutorialUI(container: HTMLElement): TutorialUI {
  const el = document.createElement('div');
  el.className = 'bv-tut';
  el.hidden = true;
  el.setAttribute('role', 'dialog');
  el.setAttribute('aria-label', 'Tutorial');
  container.appendChild(el);

  const ring = document.createElement('div');
  ring.className = 'bv-tut-ring';
  const card = document.createElement('div');
  card.className = 'bv-tut-card';
  el.append(ring, card);

  /**
   * Repositions the ring and the card over the current step's anchor.
   *
   * Split out from the render because it also has to run on RESIZE and on
   * scroll: the action bar is laid out relative to the viewport, so a window
   * the player drags narrower leaves the ring behind. Cheap enough to call on
   * every such event — two `getBoundingClientRect`s and four style writes.
   */
  const place = (): void => {
    const state = walkthroughStore.get();
    if (state.stage !== 'running') return;
    const step = TUTORIAL_STEPS[state.step];
    if (!step) return;

    const target = step.anchor ? document.querySelector<HTMLElement>(step.anchor) : null;
    if (!target) {
      // Either a step with no anchor, or one whose anchor is not in this view.
      // The ring keeps only its dimming spread (see `.bv-tut-ring-none`) and
      // the card centres — NOT hidden, because a step that silently lost its
      // highlight should still be readable rather than half-drawn.
      ring.classList.add('bv-tut-ring-none');
      ring.style.cssText = RING_NOWHERE;
      card.classList.add('bv-tut-card-center');
      card.style.top = '';
      card.style.left = '';
      return;
    }

    ring.classList.remove('bv-tut-ring-none');
    card.classList.remove('bv-tut-card-center');
    const box = target.getBoundingClientRect();
    const pad = 7;
    ring.style.top = `${box.top - pad}px`;
    ring.style.left = `${box.left - pad}px`;
    ring.style.width = `${box.width + pad * 2}px`;
    ring.style.height = `${box.height + pad * 2}px`;

    // The card goes above the anchor if there is room, otherwise below; and is
    // clamped into the viewport on both axes. Measured from the card's own
    // rect rather than assumed, because its height depends on how much prose
    // the step carries.
    const cardBox = card.getBoundingClientRect();
    const gap = 16;
    const above = box.top - gap - cardBox.height;
    const top = above >= 8 ? above : Math.min(box.bottom + gap, window.innerHeight - cardBox.height - 8);
    const left = Math.max(
      8,
      Math.min(
        box.left + box.width / 2 - cardBox.width / 2,
        window.innerWidth - cardBox.width - 8,
      ),
    );
    card.style.top = `${Math.max(8, top)}px`;
    card.style.left = `${left}px`;
  };

  const footer = (left: string, right: string): string => `
    <div class="bv-tut-foot">${left}${right}</div>`;

  const render = (state: WalkthroughState): void => {
    if (state.stage === 'idle') {
      el.hidden = true;
      card.innerHTML = '';
      return;
    }
    el.hidden = false;

    if (state.stage === 'prompt') {
      ring.classList.add('bv-tut-ring-none');
      ring.style.cssText = RING_NOWHERE;
      card.classList.add('bv-tut-card-center');
      card.style.top = '';
      card.style.left = '';
      card.innerHTML = `
        <p class="bv-tut-kicker">${PROMPT.kicker}</p>
        <h2 class="bv-tut-title">${PROMPT.title}</h2>
        ${PROMPT.body.map((p) => `<p class="bv-tut-body">${p}</p>`).join('')}
        ${footer(
    '<span class="bv-tut-count">8 cards, about two minutes</span>',
    '<button type="button" class="bv-tut-btn" data-tut="skip">Not now</button>'
          + '<button type="button" class="bv-tut-btn bv-tut-btn-go" data-tut="start">Yes, show me</button>',
  )}`;
      return;
    }

    const step = TUTORIAL_STEPS[state.step];
    if (!step) {
      // Out of range — only reachable if the step list shrank under a running
      // tour (a hot reload while it is open). Ending is the honest answer.
      endWalkthrough();
      return;
    }
    const last = state.step + 1 >= TUTORIAL_STEPS.length;
    card.innerHTML = `
      <p class="bv-tut-kicker">${step.kicker}</p>
      <h2 class="bv-tut-title">${step.title}</h2>
      ${step.body.map((p) => `<p class="bv-tut-body">${p}</p>`).join('')}
      ${footer(
    `<span class="bv-tut-count">${state.step + 1} of ${TUTORIAL_STEPS.length}</span>`,
    '<button type="button" class="bv-tut-btn bv-tut-btn-quiet" data-tut="skip">Skip</button>'
          + (state.step > 0 ? '<button type="button" class="bv-tut-btn" data-tut="prev">Back</button>' : '')
          + `<button type="button" class="bv-tut-btn bv-tut-btn-go" data-tut="next">${last ? 'Done' : 'Next'}</button>`,
  )}`;
    // After the markup, because the card's height decides where it sits.
    place();
  };

  /**
   * One delegated listener on the card, rather than one per button per render.
   *
   * The card's innerHTML is rewritten on every step, so per-button listeners
   * would be re-attached eight times and the old ones would ride the discarded
   * nodes. Delegation sidesteps the bookkeeping entirely.
   */
  const onClick = (e: Event): void => {
    const action = (e.target as HTMLElement | null)?.closest<HTMLElement>('[data-tut]')?.dataset.tut;
    if (!action) return;
    if (action === 'start') startWalkthrough();
    else if (action === 'next') nextWalkthroughStep(TUTORIAL_STEPS.length);
    else if (action === 'prev') prevWalkthroughStep();
    else if (action === 'skip') endWalkthrough();
  };

  /**
   * Escape ends the tour, and the handler runs on `window` in the CAPTURE
   * phase while the tour is open.
   *
   * Capture, and `stopPropagation`, for a specific reason: base-view's own
   * keydown listener is also on `window` and would treat the same Escape as
   * "close whatever panel is open". Both firing means pressing Escape during
   * the inventory step both ends the tour and shuts a window the player had
   * open behind it. Only the keys the tour claims are stopped — everything
   * else still reaches the game, because the tour is about letting the player
   * try the controls while they read about them.
   */
  const onKeyDown = (e: KeyboardEvent): void => {
    if (walkthroughStore.get().stage === 'idle') return;
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      endWalkthrough();
    }
  };

  card.addEventListener('click', onClick);
  window.addEventListener('keydown', onKeyDown, true);
  window.addEventListener('resize', place);
  const unsub = walkthroughStore.subscribe(render);
  render(walkthroughStore.get());

  return {
    dispose(): void {
      unsub();
      card.removeEventListener('click', onClick);
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('resize', place);
      el.remove();
      // The store is NOT reset. A Location visit tears this view down and
      // rebuilds it, and a tour the player was halfway through should still be
      // there when they come back — the fresh overlay renders whatever stage
      // the store is on. `endWalkthrough` is the only thing that closes it.
    },
  };
}
